import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useMediaUpload } from "@/hooks/useMediaUpload";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Icons } from "../ui/icons";

const mediaFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
});

type MediaFormData = z.infer<typeof mediaFormSchema>;

interface MediaUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (mediaId: string) => void;
  acceptedFileTypes?: string;
  fileTypeLabel?: string;
}

export function MediaUploadModal({
  open,
  onOpenChange,
  onSuccess,
  acceptedFileTypes = "*/*",
  fileTypeLabel,
}: MediaUploadModalProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [_createdMediaId, setCreatedMediaId] = useState<string | null>(null);

  const form = useForm<MediaFormData>({
    resolver: zodResolver(mediaFormSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const { uploadFile, isUploading } = useMediaUpload({
    onSuccess: (media) => {
      setCreatedMediaId(media.id);
      toast.success("File uploaded successfully");
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    // Auto-populate title from filename if not already set
    if (!form.getValues("title")) {
      form.setValue("title", file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const onSubmit = async (data: MediaFormData) => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      // Upload the file with the form data
      const media = await uploadFile(selectedFile, {
        title: data.title,
        description: data.description,
      });

      // Success is handled by the onSuccess callback
      queryClient.invalidateQueries(trpc.media.list.queryOptions());

      if (media?.id) {
        onSuccess(media.id);
      }

      // Reset form and close modal
      form.reset();
      setSelectedFile(null);
      setCreatedMediaId(null);
      onOpenChange(false);
    } catch (error) {
      // Error is handled by the onError callback
      console.error("Upload error:", error);
    }
  };

  const handleClose = () => {
    form.reset();
    setSelectedFile(null);
    setCreatedMediaId(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Media</DialogTitle>
          <DialogDescription>
            Upload a new file and provide details
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter media title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter media description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">File</label>
                {fileTypeLabel && (
                  <p className="text-xs text-muted-foreground">
                    {fileTypeLabel}
                  </p>
                )}
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  {selectedFile ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedFile(null)}
                        disabled={isUploading}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Choose a file to upload
                      </p>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept={acceptedFileTypes}
                          disabled={isUploading}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isUploading}
                          asChild
                        >
                          <span>Select File</span>
                        </Button>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading || !selectedFile}>
                {isUploading ? (
                  <Icons.spinner className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Upload Media
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
